import type { SelectOption, OfferDetails } from '../../lib/types';
import type { PriceSummaryElements } from './price-calculator';
import { setupSearchFilter } from './search-filter';
import { filterTransportOptions } from './transport-filter';
import { minMaxFromContainer } from './date-range';
import { setupSubRangePricing } from './sub-range-pricing';
import { setupUnitCalendarToggles } from './unit-calendar';
import { formatDate } from '../../lib/helpers';
import * as cache from './offer-cache';
import { groupDetailsHtml, groupPricesHtml } from './offer-renderers';
import { updatePriceSummary } from './price-calculator';

export interface OfferGroupsConfig {
    accommodationSelect: HTMLSelectElement;
    transportSelect: HTMLSelectElement;
    insuranceSelect: HTMLSelectElement;
    accommodationSearchBox: HTMLInputElement;
    transportSearchBox: HTMLInputElement;
    insuranceSearchBox: HTMLInputElement;
    accommodationPricesContent: HTMLElement;
    transportPricesContent: HTMLElement;
    insurancePricesContent: HTMLElement;
    accommodationDetailsPanel: HTMLElement;
    transportDetailsPanel: HTMLElement;
    insuranceDetailsPanel: HTMLElement;
    transportSection: HTMLElement;
    insuranceSection: HTMLElement;
    transportFilterInfo: HTMLElement;
    summaryElements: import('./price-calculator').PriceSummaryElements;
    urls: { getOfferDetails: string };
    getAgencyId: () => string;
    onPriceChange: () => void;
}

// Module-level transport filter state (one page, one set of state)
let _filterState = { city: '', from: '', to: '' };
let _allAccommodation: SelectOption[] = [];
let _allTransport: SelectOption[] = [];
let _allInsurance: SelectOption[] = [];

export function populateSelects(
    accommodation: SelectOption[],
    transport: SelectOption[],
    insurance: SelectOption[],
    config: Pick<OfferGroupsConfig,
        'accommodationSelect' | 'transportSelect' | 'insuranceSelect' |
        'accommodationSearchBox' | 'transportSearchBox' | 'insuranceSearchBox' |
        'accommodationPricesContent' | 'transportPricesContent' | 'insurancePricesContent' |
        'accommodationDetailsPanel' | 'transportDetailsPanel' | 'insuranceDetailsPanel' |
        'transportSection' | 'insuranceSection' | 'transportFilterInfo' |
        'summaryElements' | 'onPriceChange'>
): void {
    _allAccommodation = accommodation;
    _allTransport = transport;
    _allInsurance = insurance;
    _filterState = { city: '', from: '', to: '' };

    fillSelect(config.accommodationSelect, accommodation, ['city', 'address']);
    fillSelect(config.insuranceSelect, insurance, []);
    config.transportSelect.innerHTML = '';
    config.transportSection.classList.add('d-none');
    config.insuranceSection.classList.add('d-none');

    setEmptyPanel(config.accommodationPricesContent, 'Select an accommodation to see available dates.');
    setEmptyPanel(config.accommodationDetailsPanel, 'Select an accommodation to see details.');
    setEmptyPanel(config.transportPricesContent, 'Select a transport offer to see available prices.');
    config.transportDetailsPanel.innerHTML = '';
    setEmptyPanel(config.insurancePricesContent, 'Select an insurance offer to see available prices.');
    config.insuranceDetailsPanel.innerHTML = '';
    config.transportFilterInfo.textContent = 'Transport offers will appear once you select an accommodation.';

    updatePriceSummary(config.summaryElements);
    config.onPriceChange();
}

export function setupOfferGroups(config: OfferGroupsConfig): void {
    const {
        accommodationSelect, transportSelect, insuranceSelect,
        accommodationSearchBox, transportSearchBox, insuranceSearchBox,
        accommodationPricesContent, transportPricesContent, insurancePricesContent,
        accommodationDetailsPanel, transportDetailsPanel, insuranceDetailsPanel,
        transportSection, insuranceSection, transportFilterInfo,
        summaryElements, urls, getAgencyId, onPriceChange,
    } = config;

    _allAccommodation = readSelectOptions(accommodationSelect, ['city', 'address']);
    _allTransport = readSelectOptions(transportSelect, ['city', 'address', 'from', 'to']);
    _allInsurance = readSelectOptions(insuranceSelect, []);

    setupSearchFilter(accommodationSearchBox, accommodationSelect, () => _allAccommodation);
    setupSearchFilter(transportSearchBox, transportSelect, () => filterTransportOptions(_allTransport, _filterState));
    setupSearchFilter(insuranceSearchBox, insuranceSelect, () => _allInsurance);

    // Accommodation change
    accommodationSelect.addEventListener('change', async () => {
        const ids = selectedIds(accommodationSelect);

        if (ids.length === 0) {
            _filterState = { city: '', from: '', to: '' };
            accommodationDetailsPanel.innerHTML = '<p class="text-muted small">Select an accommodation to see details.</p>';
            setEmptyPanel(accommodationPricesContent, 'Select an accommodation to see available dates.');
            transportSection.classList.add('d-none');
            insuranceSection.classList.add('d-none');
            transportSelect.innerHTML = '';
            setEmptyPanel(transportPricesContent, 'Select a transport offer to see available prices.');
            transportDetailsPanel.innerHTML = '';
            insuranceSelect.selectedIndex = -1;
            setEmptyPanel(insurancePricesContent, 'Select an insurance offer to see available prices.');
            insuranceDetailsPanel.innerHTML = '';
            updatePriceSummary(summaryElements);
            onPriceChange();
            return;
        }

        await loadAndRender(ids, accommodationDetailsPanel, accommodationPricesContent,
            'Select an accommodation to see details.', 'Select an accommodation to see available dates.',
            urls, getAgencyId, summaryElements, onPriceChange, undefined,
            (first) => {
                _filterState.city = first?.city || '';
                const range = minMaxFromContainer(accommodationPricesContent);
                _filterState.from = range.min;
                _filterState.to = range.max;
                transportSection.classList.remove('d-none');
                insuranceSection.classList.remove('d-none');
                refreshTransportList(transportSelect, transportPricesContent, transportDetailsPanel,
                    transportSearchBox, transportFilterInfo, urls, getAgencyId, summaryElements, onPriceChange);
            }
        );
    });

    // Transport change
    transportSelect.addEventListener('change', async () => {
        const ids = selectedIds(transportSelect);
        const dateFilter = (_filterState.from && _filterState.to)
            ? { from: _filterState.from, to: _filterState.to }
            : undefined;
        await loadAndRender(ids, transportDetailsPanel, transportPricesContent,
            '', 'Select a transport offer to see available prices.',
            urls, getAgencyId, summaryElements, onPriceChange, dateFilter, null);
    });

    // Insurance change
    insuranceSelect.addEventListener('change', async () => {
        const ids = selectedIds(insuranceSelect);
        await loadAndRender(ids, insuranceDetailsPanel, insurancePricesContent,
            '', 'Select an insurance offer to see available prices.',
            urls, getAgencyId, summaryElements, onPriceChange, undefined, null);
    });

    // Wire accommodation price/date change → update transport filter state
    accommodationPricesContent.addEventListener('change', (e) => {
        const tgt = e.target as HTMLElement;
        if (tgt.classList.contains('offer-price-cb') ||
            tgt.classList.contains('sub-range-from-picker') ||
            tgt.classList.contains('sub-range-to-picker')) {
            const range = minMaxFromContainer(accommodationPricesContent);
            _filterState.from = range.min;
            _filterState.to = range.max;
            refreshTransportList(transportSelect, transportPricesContent, transportDetailsPanel,
                transportSearchBox, transportFilterInfo, urls, getAgencyId, summaryElements, onPriceChange);
        }
    });
}

// ---- internal helpers ----

async function loadAndRender(
    ids: string[],
    detailsPanel: HTMLElement,
    pricesContent: HTMLElement,
    emptyDetails: string,
    emptyPrices: string,
    urls: { getOfferDetails: string },
    getAgencyId: () => string,
    summaryElements: PriceSummaryElements,
    onPriceChange: () => void,
    dateFilter: { from: string; to: string } | undefined,
    afterLoad: ((firstOffer: OfferDetails | undefined) => void) | null
): Promise<void> {
    if (ids.length === 0) {
        detailsPanel.innerHTML = `<p class="text-muted small">${emptyDetails}</p>`;
        pricesContent.innerHTML = `<p class="text-muted small mb-0">${emptyPrices}</p>`;
        updatePriceSummary(summaryElements);
        onPriceChange();
        return;
    }

    await cache.load(ids, urls.getOfferDetails, getAgencyId());
    const all = cache.getAll();

    detailsPanel.innerHTML = groupDetailsHtml(ids, all, emptyDetails);

    const allowsSubRange = pricesContent.dataset.allowsSubRange === 'true';
    const rateUnit       = pricesContent.dataset.rateUnit       || '';
    const pricingMode    = pricesContent.dataset.pricingMode    || '';
    const group          = pricesContent.dataset.offerGroup;
    const fromLabel      = pricesContent.dataset.calI18nFrom    || 'From';
    const toLabel        = pricesContent.dataset.calI18nTo      || 'To';

    const prevChecked = allowsSubRange
        ? new Set<string>()
        : new Set(
            Array.from(pricesContent.querySelectorAll<HTMLInputElement>('[name$=".SelectedOfferPriceIds"]:checked'))
                .map(cb => cb.value)
        );

    const { html } = groupPricesHtml(ids, all, prevChecked, dateFilter, emptyPrices, group, allowsSubRange, rateUnit, pricingMode, fromLabel, toLabel);
    pricesContent.innerHTML = html;

    if (allowsSubRange) {
        setupSubRangePricing(pricesContent, pricingMode, summaryElements, onPriceChange);
        const calDataUrl = pricesContent.dataset.calendarDataUrl || '';
        if (calDataUrl) {
            const i18n = buildCalI18n(pricesContent);
            const calCurrency = pricesContent.dataset.calCurrency || 'RSD';
            setupUnitCalendarToggles(pricesContent, { dataUrl: calDataUrl, i18n, currency: calCurrency });
        }
    } else {
        pricesContent.querySelectorAll<HTMLInputElement>('.offer-price-cb').forEach(cb => {
            cb.addEventListener('change', () => {
                updatePriceSummary(summaryElements);
                onPriceChange();
            });
        });
    }

    updatePriceSummary(summaryElements);
    onPriceChange();
    if (afterLoad) afterLoad(cache.get(ids[0]));
}

function refreshTransportList(
    transportSelect: HTMLSelectElement,
    transportPricesContent: HTMLElement,
    transportDetailsPanel: HTMLElement,
    transportSearchBox: HTMLInputElement,
    transportFilterInfo: HTMLElement,
    urls: { getOfferDetails: string },
    getAgencyId: () => string,
    summaryElements: PriceSummaryElements,
    onPriceChange: () => void
): void {
    const filtered = filterTransportOptions(_allTransport, _filterState);
    const prevSelected = new Set(selectedIds(transportSelect));

    transportSelect.innerHTML = '';
    for (const item of filtered) {
        const opt = document.createElement('option');
        opt.value = item.value;
        opt.textContent = item.text;
        if (prevSelected.has(item.value)) opt.selected = true;
        transportSelect.appendChild(opt);
    }
    transportSearchBox.value = '';

    if (filtered.length === 0 && _allTransport.length > 0) {
        transportFilterInfo.textContent = 'No transport offers found for the selected accommodation location and dates. Showing all transport offers.';
        for (const item of _allTransport) {
            const opt = document.createElement('option');
            opt.value = item.value;
            opt.textContent = item.text;
            transportSelect.appendChild(opt);
        }
    } else if (_filterState.city) {
        let info = `Showing transport offers for: ${_filterState.city}`;
        if (_filterState.from && _filterState.to) {
            info += ` (${formatDate(_filterState.from)} – ${formatDate(_filterState.to)})`;
        }
        transportFilterInfo.textContent = info;
    }

    const stillSelected = selectedIds(transportSelect).filter(id => filtered.some(f => f.value === id));
    if (stillSelected.length > 0) {
        const dateFilter = (_filterState.from && _filterState.to)
            ? { from: _filterState.from, to: _filterState.to }
            : undefined;
        loadAndRender(stillSelected, transportDetailsPanel, transportPricesContent,
            '', 'Select a transport offer to see available prices.',
            urls, getAgencyId, summaryElements, onPriceChange, dateFilter, null);
    } else {
        setEmptyPanel(transportPricesContent, 'Select a transport offer to see available prices.');
        transportDetailsPanel.innerHTML = '';
    }
}

function selectedIds(el: HTMLSelectElement): string[] {
    return Array.from(el.selectedOptions).map(o => o.value);
}

function setEmptyPanel(el: HTMLElement, msg: string): void {
    el.innerHTML = `<p class="text-muted small mb-0">${msg}</p>`;
}

function fillSelect(el: HTMLSelectElement, options: SelectOption[], dataAttrs: string[]): void {
    el.innerHTML = '';
    for (const item of options) {
        const opt = document.createElement('option');
        opt.value = item.value;
        opt.textContent = item.text;
        for (const attr of dataAttrs) {
            const val = (item as any)[attr];
            if (val) opt.setAttribute(`data-${attr}`, val);
        }
        el.appendChild(opt);
    }
}

function readSelectOptions(el: HTMLSelectElement, dataAttrs: string[]): SelectOption[] {
    return Array.from(el.options).map(opt => {
        const item: SelectOption = { value: opt.value, text: opt.textContent || '' };
        for (const attr of dataAttrs) {
            (item as any)[attr] = opt.getAttribute(`data-${attr}`) || '';
        }
        return item;
    });
}

function buildCalI18n(container: HTMLElement): Record<string, string> {
    const d = container.dataset;
    return {
        night:            d.calI18nNight           || 'night',
        freeNights:       d.calI18nNights          || 'nights',
        contractId:       d.calI18nContractId      || 'Contract',
        unitAccommodation:d.calI18nUnitLabel       || 'Unit',
        unitsShown:       d.calI18nUnitsShown      || 'units shown',
        showCalendar:     d.calI18nShowCalendar    || 'Show calendar',
        hideCalendar:     d.calI18nHideCalendar    || 'Hide calendar',
        loading:          d.calI18nLoading         || 'Loading…',
        calendarError:    d.calI18nError           || 'Could not load calendar.',
    };
}
